'use client';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/atoms/carousel';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';
import { getHighlightPositions } from '@/lib/utils';

// Função para remover estilos inline do HTML
const stripInlineStyles = (html: string): string => {
  if (!html) return '';

  // Remove atributos style dos elementos
  return html.replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '');
};

interface Event {
  id: string;
  duration: string;
  schedule: string;
  description: string;
  image: string;
  slug?: string;
}

interface EventsCarouselProps {
  title?: string | React.ReactNode;
  subtitle?: string;
  buttonText?: string;
  events: Event[];
  className?: string;
  onEventClick?: (eventId: string) => void;
  highlight_title?: string | string[];
}

const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.25 0V1.5H9.75V0H11.25V1.5H14.25C14.6642 1.5 15 1.83579 15 2.25V14.25C15 14.6642 14.6642 15 14.25 15H0.75C0.33579 15 0 14.6642 0 14.25V2.25C0 1.83579 0.33579 1.5 0.75 1.5H3.75V0H5.25ZM13.5 7.5H1.5V13.5H13.5V7.5ZM4.5 9V10.5H3V9H4.5ZM8.25 9V10.5H6.75V9H8.25ZM12 9V10.5H10.5V9H12ZM3.75 3H1.5V6H13.5V3H11.25V4.5H9.75V3H5.25V4.5H3.75V3Z" fill="#377396" />
  </svg>
);

const ClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 15C3.35786 15 0 11.6421 0 7.5C0 3.35786 3.35786 0 7.5 0C11.6421 0 15 3.35786 15 7.5C15 11.6421 11.6421 15 7.5 15ZM7.5 13.5C10.8137 13.5 13.5 10.8137 13.5 7.5C13.5 4.18629 10.8137 1.5 7.5 1.5C4.18629 1.5 1.5 4.18629 1.5 7.5C1.5 10.8137 4.18629 13.5 7.5 13.5ZM8.25 7.5H11.25V9H6.75V3.75H8.25V7.5Z" fill="#377396" />
  </svg>
);

const EventCard: React.FC<{ event: Event; onExpand: () => void }> = ({ event, onExpand }) => {
  const router = useRouter();
  // Remove estilos inline da descrição
  const cleanDescription = useMemo(() => stripInlineStyles(event.description), [event.description]);

  const handleClick = () => {
    if (event.slug) {
      router.push(`/noticias-eventos/eventos/${event.slug}`);
    } else {
      onExpand();
    }
  };

  return (
    <article className="w-full pt-6 md:pt-8 bg-white rounded-3xl shadow-[0px_8px_20px_0px_rgba(139,141,142,0.15)] flex flex-col items-center gap-8 md:gap-10 lg:gap-14">
      {/* Content Section */}
      <div className="w-full flex flex-col gap-4 md:gap-6 lg:gap-8 px-3 md:px-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
          {/* Duration */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CalendarIcon />
            <span className="text-zinc-600 text-sm md:text-base font-light font-poppins">
              {event.duration}
            </span>
          </div>

          {/* Schedule */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            
            <span className="text-zinc-600 text-sm md:text-base font-light font-poppins rounded-4xl border-1 px-4 py-2 border-[#377396]">
              {event.schedule}
            </span>
          </div>

        </div>
        {/* Description */}
        <div className="text-gray-800 text-base md:text-lg lg:text-xl font-medium font-poppins leading-6 md:leading-7 lg:leading-8 line-clamp-3 md:line-clamp-4" dangerouslySetInnerHTML={{ __html: cleanDescription }} />
      </div>

      {/* Image Section with Plus Button */}
      <div className="w-full max-w-[280px] md:max-w-[320px] h-64 md:h-72 lg:h-80 relative rounded-2xl">
        <Image
          src={event.image || '/event.png'}
          alt={event.description}
          fill
          className="object-cover rounded-2xl"
          sizes="(max-width: 768px) 100vw, 320px"
        />

        {/* Plus Button */}
        <button
          type="button"
          onClick={handleClick}
          className="cursor-pointer absolute bottom-6 right-6 w-[53px] h-[53px] rounded-full bg-[#F05A49] hover:bg-[#d94839] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg focus:outline-none focus:ring-2 focus:ring-[#F05A49] focus:ring-offset-2"
          aria-label="Ver mais detalhes do evento"
        >
          <span className="text-white text-3xl font-normal font-rubik leading-[49.50px] tracking-wide">
            +
          </span>
        </button>
      </div>
    </article>
  );
};

export const EventsCarousel: React.FC<EventsCarouselProps> = ({
  title = 'Próximos Eventos',
  subtitle = 'Eventos',
  buttonText = 'Ver Todos',
  events,
  className = '',
  onEventClick,
  highlight_title = '',
}) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(0);
  const [carouselCount, setCarouselCount] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(events.length / itemsPerPage);

  // Carousel API handlers
  useEffect(() => {
    if (!carouselApi) return;

    setCarouselCount(carouselApi.scrollSnapList().length);
    setCarouselCurrentIndex(carouselApi.selectedScrollSnap());

    carouselApi.on('select', () => {
      setCarouselCurrentIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const scrollToIndex = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const handleExpand = (eventId: string) => {
    if (onEventClick) {
      onEventClick(eventId);
    }
  };

  const visibleEvents = events.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <section className={`w-full min-h-[600px] lg:min-h-[848px] bg-stone-300/20 py-10 md:py-16 px-2 md:px-8 ${className}`}>
      <div className="container mx-auto px-2 sm:px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-8 lg:gap-12 items-start">
          {/* Left Content */}
          <div className="space-y-6 md:space-y-8">

            {/* Title */}
            <div className="max-w-md lg:mt-8">
              {typeof title === 'string' ? (
                (() => {
                  const sentence = title || "";
                  const keywords = highlight_title || "";
                  const positions = getHighlightPositions(sentence, keywords);
                  return (
                    <HighlightedHeading
                      text={sentence}
                      positions={positions}
                    />
                  );
                })()
              ) : (
                <h2 className="text-grey-dark-1 text-4xl font-normal font-['Rubik']">
                  {title}
                </h2>
              )}
            </div>

            {/* Subtitle Badge */}
            <div className="inline-block bg-stone-300/20 rounded-[30px] px-6 py-2">
              <span className="text-gray-700 text-lg font-normal font-poppins">
                {subtitle}
              </span>
            </div>

            {/* Button - Desktop */}
            <button
              type="button"
              onClick={() => router.push('/noticias-eventos')}
              className="hidden lg:flex cursor-pointer rounded-[30px] border-2 border-[#F05A49] mt-8 px-8 py-2 items-center gap-2 hover:bg-[#F05A49] hover:text-white transition-colors group"
            >
              <span className="text-[#F05A49] group-hover:text-white text-xl font-medium font-poppins capitalize">
                {buttonText}
              </span>
            </button>
          </div>

          {/* Right Content - Carousel */}
          <div className="relative">

            {/* Desktop: Grid com paginação (apenas xl+) */}
            <div className="hidden xl:block">
              <div className="grid xl:grid-cols-3 gap-6 xl:gap-8 justify-items-center">
                {visibleEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onExpand={() => handleExpand(event.id)}
                  />
                ))}
              </div>
              {/* Pagination Dots - Desktop */}
              {totalPages > 1 && (
                <div className="flex justify-start gap-4 mt-12">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`rounded-full transition-all cursor-pointer ${index === currentIndex
                        ? 'w-12 h-4 bg-[#377396]'
                        : 'w-4 h-4 bg-gray-300 hover:bg-gray-400'
                        }`}
                      aria-label={`Ir para página ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Mobile e Tablet: Carrossel */}
            <div className="xl:hidden">
              <Carousel className="w-full" setApi={setCarouselApi}>
                <CarouselContent className="-ml-2 md:-ml-4">
                  {events.map((event) => (
                    <CarouselItem key={event.id} className="pl-2 md:pl-4 basis-[85%] sm:basis-[75%] md:basis-[60%] lg:basis-[45%]">
                      <EventCard
                        event={event}
                        onExpand={() => handleExpand(event.id)}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              {/* Pagination Dots - Mobile e Tablet */}
              {carouselCount > 1 && (
                <div className="flex justify-center gap-2 sm:gap-3 mt-6">
                  {Array.from({ length: carouselCount }).map((_, index) => (
                    <button
                      key={`events-dot-${index}`}
                      onClick={() => scrollToIndex(index)}
                      className={`cursor-pointer rounded-full transition-all ${index === carouselCurrentIndex
                        ? 'w-10 sm:w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                        }`}
                      aria-label={`Ir para slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
              {/* Button - Mobile */}
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => router.push('/noticias-eventos')}
                  className="cursor-pointer rounded-[30px] border-2 border-[#F05A49] px-8 py-2 flex items-center gap-2 hover:bg-[#F05A49] hover:text-white transition-colors group"
                >
                  <span className="text-[#F05A49] group-hover:text-white text-xl font-medium font-poppins capitalize">
                    {buttonText}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};